import { useState, useCallback, useEffect, useRef } from 'react';
import { Send, Lightbulb, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

// Client-side rate limiting as first layer (server enforces too)
const RATE_KEY = 'engicoro-feature-req-rate';
const CLIENT_MAX = 3;
const CLIENT_WINDOW_MS = 60 * 60 * 1000; // 1 hour
const CLIENT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

interface RateState {
  timestamps: number[];
  cooldownUntil: number;
}

function loadRateState(): RateState {
  try {
    const raw = localStorage.getItem(RATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { timestamps: [], cooldownUntil: 0 };
}

function saveRateState(state: RateState) {
  localStorage.setItem(RATE_KEY, JSON.stringify(state));
}

function checkClientRate(): { allowed: boolean; retryHours?: number } {
  const now = Date.now();
  const state = loadRateState();

  if (state.cooldownUntil > now) {
    const hours = Math.ceil((state.cooldownUntil - now) / (60 * 60 * 1000));
    return { allowed: false, retryHours: hours };
  }

  // Clean old timestamps outside the window
  const recent = state.timestamps.filter((t) => now - t < CLIENT_WINDOW_MS);

  if (recent.length >= CLIENT_MAX) {
    state.cooldownUntil = now + CLIENT_COOLDOWN_MS;
    state.timestamps = recent;
    saveRateState(state);
    return { allowed: false, retryHours: 24 };
  }

  return { allowed: true };
}

function recordRequest() {
  const state = loadRateState();
  state.timestamps.push(Date.now());
  saveRateState(state);
}

type Status = 'idle' | 'sending' | 'success' | 'error' | 'exists' | 'rate_limited';

interface FeatureRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional snapshot-getter. When provided, the caller's rolling action-log
   * tail is attached to the submission's `metadata.recent_actions`. Nothing
   * is sent if undefined — keeps the call site opt-in.
   */
  getSnapshot?: () => unknown;
}

export function FeatureRequestDialog({ open, onOpenChange, getSnapshot }: FeatureRequestDialogProps) {
  const isMobile = useIsMobile();
  const [request, setRequest] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = request.trim();
    if (!trimmed || trimmed.length < 10) {
      setStatus('error');
      setMessage('Please provide a more detailed description (at least 10 characters).');
      return;
    }

    // Client-side rate check
    const rateCheck = checkClientRate();
    if (!rateCheck.allowed) {
      setStatus('rate_limited');
      setMessage(
        `You have reached the maximum number of feature requests. Please try again in ${rateCheck.retryHours} hour${rateCheck.retryHours !== 1 ? 's' : ''}.`
      );
      return;
    }

    setStatus('sending');
    setMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const url = `${supabaseUrl}/functions/v1/feature-request`;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          request: trimmed,
          metadata: getSnapshot ? {
            recent_actions: getSnapshot(),
            user_agent: navigator.userAgent,
            viewport: { w: window.innerWidth, h: window.innerHeight },
          } : undefined,
        }),
        signal: controller.signal,
      });

      const data = await resp.json();

      if (resp.ok && data.success) {
        recordRequest();
        setStatus('success');
        setMessage(data.message || 'Thank you for your feature request! We will look into it.');
        setRequest('');
      } else if (resp.status === 429 || data.error === 'rate_limited') {
        setStatus('rate_limited');
        setMessage(data.message || 'Too many requests. Please try again later.');
      } else if (resp.status === 409 || data.error === 'feature_exists') {
        setStatus('exists');
        setMessage(data.message || "Can't send feature request because it already exists.");
      } else {
        setStatus('error');
        setMessage(data.message || data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setStatus('error');
      setMessage('Could not connect to the server. Please check your connection and try again.');
    }
  }, [request, getSnapshot]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    // Reset after a short delay so the user doesn't see the flash
    setTimeout(() => {
      if (status !== 'rate_limited') {
        setStatus('idle');
        setMessage('');
      }
    }, 300);
  }, [onOpenChange, status]);

  const formContent = (
    <div className="flex flex-col gap-4 p-6">
      <div className="text-center mb-2">
        <Lightbulb className="w-8 h-8 mx-auto mb-2 text-amber-500" />
        <h3 className="font-semibold text-base">Feature Request</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Have an idea to improve EngiCoro Flow Weaver? Let us know!
        </p>
      </div>

      {/* Status messages */}
      {status === 'success' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-700 dark:text-green-400">Request submitted!</p>
            <p className="text-xs text-muted-foreground mt-1">{message}</p>
          </div>
        </div>
      )}

      {status === 'exists' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Feature already exists</p>
            <p className="text-xs text-muted-foreground mt-1">{message}</p>
          </div>
        </div>
      )}

      {status === 'rate_limited' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <Clock className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Request limit reached</p>
            <p className="text-xs text-muted-foreground mt-1">{message}</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{message}</p>
        </div>
      )}

      {/* Form */}
      {status !== 'rate_limited' && (
        <>
          <Textarea
            placeholder="Describe the feature you'd like to see... Be as specific as possible."
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={status === 'sending' || status === 'success'}
            className="resize-none"
          />

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {request.length}/2000 characters
            </span>
            <Button
              onClick={handleSubmit}
              disabled={status === 'sending' || status === 'success' || request.trim().length < 10}
              className="gap-2"
            >
              {status === 'sending' ? (
                <>
                  <span className="animate-spin w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full" />
                  Analyzing...
                </>
              ) : status === 'success' ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Sent!
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="bottom" className="p-0">
          <SheetTitle className="sr-only">Feature request</SheetTitle>
          {formContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Feature request</DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
